import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CompanyBrandMark from '../../components/layout/CompanyBrandMark';
import MyAttendance from './MyAttendance';
import MyLeaves from './MyLeaves';
import MyPayslips from './MyPayslips';
import MyProfile from './MyProfile';

const EmployeePortalLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('employeeUser');
    const token = localStorage.getItem('employeeToken');
    if (!token || !savedUser) {
      navigate('/employee/login');
      return;
    }
    try {
      setEmployee(JSON.parse(savedUser));
    } catch (e) {}
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('employeeToken');
    localStorage.removeItem('employeeUser');
    window.dispatchEvent(new Event('authChange'));
    navigate('/employee/login');
  };

  const navItems = [
    { path: '/employee/portal/attendance', label: 'My Attendance', icon: 'Clock' },
    { path: '/employee/portal/leaves', label: 'My Leaves', icon: 'Calendar' },
    { path: '/employee/portal/payslips', label: 'My Payslips', icon: 'FileText' },
    { path: '/employee/portal/profile', label: 'My Profile', icon: 'User' }
  ];

  const initials = (employee?.full_name || 'EMP')
    .split(' ')
    .map(p => p[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  return (
    <div className="min-h-screen bg-muted/20 flex flex-col">
      {/* Top Employee Header */}
      <header className="bg-card border-b border-border sticky top-0 z-40 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/employee/portal/attendance">
              <CompanyBrandMark size="md" />
            </Link>
            <span className="hidden sm:inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-500/10 text-teal-600 border border-teal-500/20">
              Employee Portal
            </span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-500/10 text-teal-600 font-bold text-xs flex items-center justify-center border border-teal-500/20 overflow-hidden">
                {employee?.face_photo_url ? (
                  <img src={employee.face_photo_url} alt={employee.full_name} className="w-full h-full object-cover" />
                ) : (
                  initials
                )}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-xs font-bold text-foreground leading-snug">{employee?.full_name || 'Employee'}</p>
                <p className="text-[10px] text-muted-foreground font-mono">{employee?.employee_code || ''}</p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              iconName="LogOut"
              iconPosition="left"
              onClick={handleLogout}
              className="text-xs border-rose-500/30 text-rose-600 hover:bg-rose-500/10 font-semibold"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row gap-6">
        {/* Navigation Tabs (Sidebar on Desktop, Horizontal Tabs on Mobile) */}
        <aside className="w-full md:w-64 shrink-0">
          <div className="bg-card border border-border rounded-xl p-3 shadow-sm space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-semibold transition-colors ${
                    isActive
                      ? 'bg-teal-600 text-white shadow-xs'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item.icon} size={16} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </aside>

        {/* Portal Page Content */}
        <main className="flex-1 min-w-0">
          <Routes>
            <Route path="attendance" element={<MyAttendance />} />
            <Route path="leaves" element={<MyLeaves />} />
            <Route path="payslips" element={<MyPayslips />} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="*" element={<Navigate to="attendance" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default EmployeePortalLayout;
